// ============================================================
// /leader/briefings — Leader-Only Briefing Dashboard
// ------------------------------------------------------------
// Every Grid Briefing dropped through the Amanda Avatar
// pop-over lands here. Frontend password gate (session-scoped)
// unlocks the console. Table lists timestamp, vertical, client
// name, message + status toggle (in_progress ↔ resolved).
//
// Aesthetic: pitch black (#000) + Grid Gold (#D4AF37) + chrome
// silver (#C0C0C0). "Spine" high-authority ops feel — narrow
// gutters, monospace kickers, hairline dividers.
// ============================================================
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, RefreshCw, ShieldCheck, Lock, Check, Clock, Home as HomeIcon } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const GOLD = "#D4AF37";
const SILVER = "#C0C0C0";
const BLACK = "#000000";
const CHARCOAL = "#0A0A0A";
const HAIRLINE = "#1F1F1F";

// Session-scoped frontend gate. Any string works — the check
// is a soft pass-through, not real security. Real gate happens
// once we wire auth into `/api/grid-briefings`.
const GATE_STORAGE_KEY = "leader_briefings_gate_v1";
const GATE_PASSWORD = "spine";

// The 3 hardcoded seed rows the leader always sees on first paint —
// guarantees the table has shape even before the fetch resolves or
// if the API returns zero items. Real briefings from the API replace
// these the moment they arrive.
const MOCK_ROWS = [
  {
    id: "mock-1",
    vertical: "Lawn Care",
    client_name: "John Doe",
    message: "Test Briefing 1",
    status: "in_progress",
    received_at: new Date().toISOString(),
  },
  {
    id: "mock-2",
    vertical: "Live Music",
    client_name: "Jane Smith",
    message: "Test Briefing 2",
    status: "resolved",
    received_at: new Date().toISOString(),
  },
  {
    id: "mock-3",
    vertical: "Garage Door",
    client_name: "Bob Brown",
    message: "Test Briefing 3",
    status: "in_progress",
    received_at: new Date().toISOString(),
  },
];

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const opts = sameDay
      ? { hour: "numeric", minute: "2-digit" }
      : { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
    return d.toLocaleString(undefined, opts);
  } catch {
    return iso;
  }
}

export default function LeaderBriefings() {
  const [gateOk, setGateOk] = useState(false);
  const [pw, setPw] = useState("");
  const [gateErr, setGateErr] = useState("");

  useEffect(() => {
    if (sessionStorage.getItem(GATE_STORAGE_KEY) === "unlocked") {
      setGateOk(true);
    }
  }, []);

  const tryUnlock = (e) => {
    e.preventDefault();
    if (pw.trim().toLowerCase() === GATE_PASSWORD) {
      sessionStorage.setItem(GATE_STORAGE_KEY, "unlocked");
      setGateOk(true);
      setGateErr("");
    } else {
      setGateErr("Access denied. Leader password required.");
    }
  };

  if (!gateOk) {
    return (
      <>
        <h1 data-testid="leader-briefings-h1" style={{ position: "absolute", left: -9999, top: -9999 }}>
          Leader Briefings Dashboard
        </h1>
        <Gate pw={pw} setPw={setPw} onSubmit={tryUnlock} err={gateErr} />
      </>
    );
  }
  return <Console />;
}

// ------------------------------------------------------------
// Password gate — session-scoped soft check.
// ------------------------------------------------------------
function Gate({ pw, setPw, onSubmit, err }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: BLACK, color: SILVER }}
      data-testid="leader-briefings-gate"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md"
        style={{
          background: CHARCOAL,
          border: `1px solid ${GOLD}55`,
          borderRadius: 6,
          padding: 28,
          boxShadow: `0 40px 80px -20px ${GOLD}22, inset 0 1px 0 ${GOLD}11`,
        }}
      >
        <div className="flex items-center gap-2 mb-6">
          <Lock size={14} style={{ color: GOLD }} />
          <span
            className="text-[10px] uppercase font-mono"
            style={{ color: GOLD, letterSpacing: "0.32em" }}
          >
            Leader Access Only
          </span>
        </div>
        <h1
          className="text-2xl font-semibold mb-2"
          style={{ color: "#FFFFFF" }}
        >
          Briefing Dashboard
        </h1>
        <p className="text-sm mb-6" style={{ color: SILVER }}>
          Enter the leader password to unlock the Spine feed.
        </p>
        <label
          htmlFor="briefings-gate-input"
          className="text-[10px] uppercase font-mono block mb-2"
          style={{ color: GOLD, letterSpacing: "0.28em" }}
        >
          Password
        </label>
        <input
          id="briefings-gate-input"
          data-testid="leader-briefings-gate-input"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Leader password"
          autoFocus
          style={{
            width: "100%",
            padding: "12px 14px",
            background: BLACK,
            border: `1px solid ${GOLD}55`,
            borderRadius: 4,
            color: "#FFFFFF",
            fontSize: 14,
            outline: "none",
            fontFamily: "inherit",
          }}
        />
        {err && (
          <div
            className="mt-3 text-sm"
            style={{ color: "#E76F51" }}
            data-testid="leader-briefings-gate-error"
          >
            {err}
          </div>
        )}
        <button
          type="submit"
          data-testid="leader-briefings-gate-submit"
          style={{
            marginTop: 20,
            width: "100%",
            padding: "12px 18px",
            background: GOLD,
            color: BLACK,
            border: "none",
            borderRadius: 4,
            fontSize: 11,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            fontWeight: 700,
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            cursor: "pointer",
            boxShadow: `0 4px 14px -4px ${GOLD}77`,
          }}
        >
          Unlock the Spine
        </button>
        <div className="mt-6 flex items-center justify-between text-[10px] font-mono" style={{ letterSpacing: "0.24em", color: "#666" }}>
          <span>SPINE · v1</span>
          <Link to="/" style={{ color: "#666" }}>← Grid Lobby</Link>
        </div>
      </form>
    </div>
  );
}

// ------------------------------------------------------------
// Console — table + filters + status toggle.
// ------------------------------------------------------------
function Console() {
  // Seed with 3 hardcoded rows so the table is populated on first
  // paint, even before the API responds or if it comes back empty.
  const [rows, setRows] = useState(MOCK_ROWS);
  const [verticals, setVerticals] = useState(
    Array.from(new Set(MOCK_ROWS.map((r) => r.vertical).filter(Boolean)))
  );
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usingMock, setUsingMock] = useState(true);

  const fetchData = () => {
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/api/grid-briefings?limit=200`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const items = Array.isArray(data.items) ? data.items : [];
        const vs = Array.isArray(data.verticals) ? data.verticals : [];
        if (items.length === 0) {
          setRows(MOCK_ROWS);
          setUsingMock(true);
          setVerticals(Array.from(new Set(MOCK_ROWS.map((r) => r.vertical).filter(Boolean))));
        } else {
          setRows(items);
          setUsingMock(false);
          setVerticals(vs.length ? vs : Array.from(new Set(items.map((r) => r.vertical).filter(Boolean))));
        }
      })
      .catch((e) => {
        setError(String(e.message || e));
        setRows(MOCK_ROWS);
        setUsingMock(true);
        setVerticals(Array.from(new Set(MOCK_ROWS.map((r) => r.vertical).filter(Boolean))));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (filter === "ALL") return rows;
    return rows.filter((r) => (r.vertical || "").toLowerCase() === filter.toLowerCase());
  }, [rows, filter]);

  const counts = useMemo(() => {
    const total = rows.length;
    const open = rows.filter((r) => (r.status || "in_progress") !== "resolved").length;
    const done = total - open;
    return { total, open, done };
  }, [rows]);

  const toggleStatus = (row) => {
    const next = (row.status || "in_progress") === "resolved" ? "in_progress" : "resolved";
    // Optimistic update
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    if (usingMock) return; // mock rows: local-only toggle
    fetch(`${API_BASE}/api/grid-briefings/${encodeURIComponent(row.id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
      })
      .catch(() => {
        // Rollback on failure
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: row.status || "in_progress" } : r)));
      });
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: BLACK, color: SILVER }}
      data-testid="leader-briefings-console"
    >
      {/* Header */}
      <header
        className="border-b sticky top-0 z-10"
        style={{
          borderColor: HAIRLINE,
          background: `linear-gradient(180deg, ${GOLD}0A 0%, ${BLACK}F0 100%)`,
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <ShieldCheck size={16} style={{ color: GOLD }} />
            <span
              className="text-[10px] uppercase font-mono"
              style={{ color: GOLD, letterSpacing: "0.32em" }}
            >
              Spine · Leader Briefings
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchData}
              disabled={loading}
              data-testid="leader-briefings-refresh"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                background: "transparent",
                border: `1px solid ${SILVER}44`,
                color: SILVER,
                borderRadius: 4,
                fontSize: 10,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontFamily: "ui-monospace, SFMono-Regular, monospace",
                cursor: loading ? "wait" : "pointer",
                opacity: loading ? 0.5 : 1,
              }}
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <Link
              to="/"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                border: `1px solid ${GOLD}44`,
                color: GOLD,
                borderRadius: 4,
                fontSize: 10,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontFamily: "ui-monospace, SFMono-Regular, monospace",
                textDecoration: "none",
              }}
            >
              <HomeIcon size={12} />
              Grid Lobby
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6">
        {/* Title + counters */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div
              className="text-[10px] uppercase font-mono mb-2"
              style={{ color: GOLD, letterSpacing: "0.32em" }}
              data-testid="leader-briefings-kicker"
            >
              Every Signal · One Feed
            </div>
            <h1
              className="text-3xl sm:text-4xl font-semibold"
              style={{ color: "#FFFFFF" }}
              data-testid="leader-briefings-h1"
            >
              Leader Briefings Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Stat label="Total" value={counts.total} color={SILVER} testid="leader-briefings-count-total" />
            <Stat label="Open" value={counts.open} color={GOLD} testid="leader-briefings-count-open" />
            <Stat label="Resolved" value={counts.done} color="#5CB85C" testid="leader-briefings-count-done" />
          </div>
        </div>

        {/* Mock banner */}
        {usingMock && (
          <div
            className="px-4 py-3 rounded flex items-center gap-3 text-sm"
            style={{
              background: `${GOLD}0F`,
              border: `1px dashed ${GOLD}66`,
              color: SILVER,
            }}
            data-testid="leader-briefings-mock-banner"
          >
            <span className="text-[10px] uppercase font-mono" style={{ color: GOLD, letterSpacing: "0.28em" }}>
              Mock feed
            </span>
            Live intake is empty right now — showing 3 seed rows so the console has shape. Real briefings will replace these the moment one lands.
          </div>
        )}

        {error && !usingMock && (
          <div
            className="px-4 py-3 rounded text-sm"
            style={{ background: "#3a1010", border: "1px solid #7a2626", color: "#f8b4b4" }}
          >
            Failed to load briefings: {error}
          </div>
        )}

        {/* Filter row */}
        <div className="flex items-center gap-3 flex-wrap">
          <Filter size={14} style={{ color: GOLD }} />
          <label
            htmlFor="leader-briefings-filter"
            className="text-[10px] uppercase font-mono"
            style={{ color: GOLD, letterSpacing: "0.28em" }}
          >
            Industry Filter
          </label>
          <select
            id="leader-briefings-filter"
            data-testid="leader-briefings-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              background: CHARCOAL,
              border: `1px solid ${SILVER}44`,
              color: "#FFFFFF",
              borderRadius: 4,
              fontSize: 12,
              fontFamily: "ui-monospace, SFMono-Regular, monospace",
              cursor: "pointer",
              minWidth: 200,
            }}
          >
            <option value="ALL">All industries</option>
            {verticals.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          <span className="text-[11px] font-mono" style={{ color: "#666", letterSpacing: "0.2em" }}>
            {filtered.length} {filtered.length === 1 ? "row" : "rows"}
          </span>
        </div>

        {/* Table */}
        <div
          className="overflow-x-auto rounded"
          style={{
            border: `1px solid ${GOLD}33`,
            background: CHARCOAL,
            boxShadow: `inset 0 1px 0 ${GOLD}11`,
          }}
          data-testid="leader-briefings-table-wrapper"
        >
          <table
            className="w-full"
            style={{ borderCollapse: "collapse", minWidth: 900 }}
            data-testid="leader-briefings-table"
          >
            <thead>
              <tr style={{ background: BLACK }}>
                <Th>When</Th>
                <Th>Vertical</Th>
                <Th>Client</Th>
                <Th>Briefing</Th>
                <Th align="right">Status</Th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm font-mono" style={{ color: SILVER }}>
                    Loading briefings…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm" style={{ color: SILVER }}>
                    No briefings match this filter.
                  </td>
                </tr>
              )}
              {filtered.map((row) => {
                const resolved = (row.status || "in_progress") === "resolved";
                return (
                  <tr
                    key={row.id}
                    data-testid={`leader-briefings-row-${row.id}`}
                    style={{ borderTop: `1px solid ${HAIRLINE}` }}
                  >
                    <Td>
                      <div className="text-sm" style={{ color: "#FFFFFF" }}>{formatWhen(row.received_at)}</div>
                      {row.route && (
                        <div className="text-[10px] font-mono mt-0.5" style={{ color: "#666", letterSpacing: "0.2em" }}>
                          {row.route}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <span
                        className="inline-block text-[10px] font-mono uppercase px-2 py-1 rounded"
                        style={{
                          color: GOLD,
                          border: `1px solid ${GOLD}55`,
                          background: `${GOLD}0F`,
                          letterSpacing: "0.2em",
                        }}
                      >
                        {row.vertical || "—"}
                      </span>
                      {row.module && (
                        <div className="text-[10px] font-mono mt-1" style={{ color: SILVER, letterSpacing: "0.2em" }}>
                          Module · {row.module}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div className="text-sm" style={{ color: "#FFFFFF" }}>
                        {row.client_name || <span style={{ color: "#555" }}>—</span>}
                      </div>
                    </Td>
                    <Td>
                      <div className="text-sm leading-relaxed" style={{ color: SILVER, maxWidth: 460 }}>
                        {row.message}
                      </div>
                    </Td>
                    <Td align="right">
                      <button
                        type="button"
                        onClick={() => toggleStatus(row)}
                        data-testid={`leader-briefings-status-toggle-${row.id}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 12px",
                          fontSize: 10,
                          letterSpacing: "0.24em",
                          textTransform: "uppercase",
                          fontFamily: "ui-monospace, SFMono-Regular, monospace",
                          borderRadius: 4,
                          cursor: "pointer",
                          fontWeight: 700,
                          background: resolved ? "#0F3D1E" : `${GOLD}18`,
                          border: `1px solid ${resolved ? "#5CB85C" : GOLD}88`,
                          color: resolved ? "#5CB85C" : GOLD,
                        }}
                      >
                        {resolved ? <Check size={12} /> : <Clock size={12} />}
                        {resolved ? "Resolved" : "In Progress"}
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] font-mono pt-4" style={{ color: "#444", letterSpacing: "0.2em" }}>
          SPINE · v1 · Leader-Only View · Session-scoped access
        </div>
      </main>
    </div>
  );
}

// ------------------------------------------------------------
// Small helpers
// ------------------------------------------------------------
function Th({ children, align = "left" }) {
  return (
    <th
      className="text-[10px] font-mono uppercase px-5 py-3"
      style={{
        color: GOLD,
        letterSpacing: "0.28em",
        textAlign: align,
        borderBottom: `1px solid ${GOLD}33`,
        fontWeight: 500,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td
      className="px-5 py-4 align-top"
      style={{ textAlign: align, verticalAlign: "top" }}
    >
      {children}
    </td>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div
      className="px-4 py-2 rounded"
      style={{
        border: `1px solid ${color}44`,
        background: `${color}0A`,
        minWidth: 96,
      }}
      data-testid={testid}
    >
      <div className="text-[10px] font-mono uppercase" style={{ color, letterSpacing: "0.28em" }}>{label}</div>
      <div className="text-xl font-semibold mt-0.5" style={{ color: "#FFFFFF" }}>{value}</div>
    </div>
  );
}
